import { type ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { pick } from "lodash";
import { type AppDispatch } from "../../../../Application/globaltypes/redux";

import { type AccountBase, type PackName } from "../../../../../interfaces";
import { TagsEnumCamelPlural } from "../../../../../interfaces/TagsEnum";
import { type FiltersMap, type GenericFiltersMap, mapFilterOption } from "../../../../../utils/filterUtils";
import { EmbeddedVideosFiltersEnum } from "../../types/models";
import {
  setError as setOverviewError,
  setFilterOptions as setOverviewFilterOptions,
  setIsLoading as setOverviewIsLoading,
} from "../EmbeddedVideosFiltersSlice";

import {
  setError as setContentAssignmentError,
  setFilterOptions as setContentAssignmentFilterOptions,
  setIsLoading as setContentAssignmentIsLoading,
} from "../../../../People/ContentAssignments/state/slices/contentAssignmentsFiltersSlice";

import PromiseStatuses from "../../../../../enums/promiseStatuses";
import objUtils from "../../../../../utils/objectUtils";
import DataService from "../../../../Application/services/dataServices/typedDataService";

export interface GetFilterOptionsProps {
  showPurchased?: boolean;
  filters?: EmbeddedVideosFiltersEnum[];
}

interface BaseFilterOptionsProps {
  begin: () => void;
  success: (filterOptions: FiltersMap) => void;
  failure: (error: Error) => void;
}

const defaultFiltersOptions = [
  EmbeddedVideosFiltersEnum.Publishers,
  EmbeddedVideosFiltersEnum.Tags,
  EmbeddedVideosFiltersEnum.Packs,
  EmbeddedVideosFiltersEnum.Visibility,
];

export const getFilterOptions = async (
  props: BaseFilterOptionsProps & GetFilterOptionsProps,
  includePublishers: boolean = true,
) => {
  const { showPurchased, begin, success, failure } = props;
  const selectedFilters = !props.filters?.length ? defaultFiltersOptions : props.filters;

  let filterOptions: FiltersMap = {};

  const tagToFilterMap: { [key in TagsEnumCamelPlural]?: EmbeddedVideosFiltersEnum } = {
    [TagsEnumCamelPlural.Label]: EmbeddedVideosFiltersEnum.Labels,
    [TagsEnumCamelPlural.SoftwareApplication]: EmbeddedVideosFiltersEnum.SoftwareApplications,
  };

  const readPublishersData = async () => {
    const publishers = await DataService.embeddedVideos.getPublishersFilterOptions();
    filterOptions = mapFilterOption(
      filterOptions,
      publishers.data,
      EmbeddedVideosFiltersEnum.Publishers,
      (item: PackName) => ({
        text: item.name,
        value: item.id,
      }),
    );
  };

  const readVisibilityOptions = async () => {
    filterOptions = mapFilterOption(
      filterOptions,
      [
        { text: "Hidden", value: "false" },
        { text: "Visible", value: "true" },
      ],
      EmbeddedVideosFiltersEnum.Visibility,
      (item) => item,
    );
  };

  const readPacksData = async () => {
    const packs = await DataService.packs.getPacksFilterOptions("embeddedVideo", !!showPurchased);
    filterOptions = mapFilterOption(
      filterOptions,
      packs.data,
      EmbeddedVideosFiltersEnum.Packs,
      (item: AccountBase) => ({
        text: item.name,
        value: item.id,
      }),
    );
  };

  const readTagsData = async () => {
    const tags = await DataService.embeddedVideos.getTagsFilterOptions(!!showPurchased);
    for (const [key, value] of objUtils.typedEntries<TagsEnumCamelPlural, string[]>(tags.data)) {
      if (tagToFilterMap[key]) {
        filterOptions = mapFilterOption(filterOptions, value, tagToFilterMap[key], (item: string) => ({
          text: item,
          value: item,
        }));
      }
    }
  };

  const filterHandlers: { [key in EmbeddedVideosFiltersEnum]?: () => Promise<void> } = {
    [EmbeddedVideosFiltersEnum.Packs]: readPacksData,
    [EmbeddedVideosFiltersEnum.Tags]: readTagsData,
    [EmbeddedVideosFiltersEnum.Visibility]: readVisibilityOptions,
    ...(includePublishers && { [EmbeddedVideosFiltersEnum.Publishers]: readPublishersData }),
  };

  begin();

  const filtersHandlersPromises = Object.values(pick(filterHandlers, selectedFilters)).map(
    (handler: () => Promise<void>) => handler(),
  );

  const result = await Promise.allSettled(filtersHandlersPromises);

  let someFulfilled = false;
  result.forEach((item) => {
    ({
      [PromiseStatuses.rejected]: () => failure((item as PromiseRejectedResult).reason),
      [PromiseStatuses.fulfilled]: () => (someFulfilled = true),
    })[item.status]();
  });

  if (someFulfilled) {
    success(filterOptions);
  }
};

const getFilterOptionsWrapper = (
  setIsLoading: ActionCreatorWithPayload<boolean>,
  setFilterOptions: ActionCreatorWithPayload<GenericFiltersMap<string>>,
  setError: ActionCreatorWithPayload<Error>,
) => {
  return (dispatch: AppDispatch) =>
    getFilterOptions({
      showPurchased: true,
      filters: [
        EmbeddedVideosFiltersEnum.Publishers,
        EmbeddedVideosFiltersEnum.Tags,
        EmbeddedVideosFiltersEnum.Packs,
        EmbeddedVideosFiltersEnum.Visibility,
      ],
      begin: () => dispatch(setIsLoading(true)),
      success: (filterOptions: FiltersMap) => {
        dispatch(setFilterOptions(filterOptions));
        dispatch(setIsLoading(false));
      },
      failure: (error: Error) => {
        dispatch(setError(error));
        dispatch(setIsLoading(false));
      },
    });
};

export const getOverviewFilterOptions = () =>
  getFilterOptionsWrapper(setOverviewIsLoading, setOverviewFilterOptions, setOverviewError);

export const getContentAssignmentEmbeddedVideosFilterOptions = () =>
  /* istanbul ignore next */
  getFilterOptionsWrapper(setContentAssignmentIsLoading, setContentAssignmentFilterOptions, setContentAssignmentError);
